import { SymbolWinData } from "@lib/titan2-slingo/dist/api/model/data/SymbolWinData";
import { SymbolType } from "@lib/titan2-slingo/dist/api/model/enum/SymbolType";
import { SlingoEvent, TestEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { Test } from "@lib/titan2-slingo/dist/game/view/abstract/display/Test";
import { DataObject } from "@lib/titan2/dist/api/core/data/DataObject";
import { AppData } from "@lib/titan2/dist/game/AppData";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import { GraphicsUtil } from "@lib/titan2/dist/util/view/GraphicsUtil";
import { Texture, utils } from "pixi.js";
import { GameEvent, GameTestEvent } from "../../../controller/event/GameEvent";
import { ChanceGameData } from "../../../model/data/ChanceGameData";
import { CollectWinResultData } from "../../../model/data/CollectWinResultData";
import { ExtendedSpinResultData } from "../../../model/data/ExtendedSpinResultData";
import { WinLineData } from "../../../model/data/WinLineData";
import { ApiConstants } from "../../../model/enum/ApiConstants";
import { GameStyle } from "../style/GameStyle";
/**
 * GameTest.ts
 */
export class GameTest extends Test {
    constructor() {
        super(...arguments);
        this._gameSummaryVariant = 0;
    }
    createButtons() {
        this.generateButtonTextures();
        // Create the button to show the test buttons.
        this.createTestButton(`Show\n(${AppData.VERSION})`, this.handleShow);
        this.showButton = this.panel.getPanelChildAt(0);
        this.panel.removeAll();
        this.addChild(this.showButton);
        // Create the buttons for the test.
        super.createButtons();
        this.createTestButton("Chance Game", this.handleChanceGame);
        this.createTestButton("Chance Game Major", this.handleChanceGameMajor);
        this.createTestButton("Win Line", this.handleWinLine);
        this.createTestButton("Total win update", this.handleTotalWin);
        this.createTestButton("Random Free Spin Collection", this.handleFreeSpinAwarded);
        this.createTestButton("Trigger Wild Particles", () => this.dispatchGame(GameTestEvent.TRIGGER_WILD_PARTICLES));
        this.createTestButton("Summary slingo", this.handleSlingoOnlyResult);
        this.createTestButton("Summary Slingo and Scatter", this.handleSlingoAndScatterResult);
        this.createTestButton("Summary Slingo, Scatter and WinLine", this.handleSlingoScatterAndWinLineResult);
        this.createTestButton(`Hide\n(${AppData.VERSION})`, this.handleHide);
        this.testButtons = this.panel.getChildren().slice();
    }
    postCreate() {
        this.handleHide();
    }
    /**
     * Generate textures for the test buttons so that they don't need to be included in the production atlas.
     */
    generateButtonTextures() {
        const states = [0xaaaaaa, 0xbbbbbb, 0xcccccc, 0xdddddd];
        for (const orientation of ["Landscape", "Portrait"]) {
            const properties = this.getCurrentProperties(orientation);
            const size = properties.testButtonProperties.textFieldProperties.size;
            const radius = Math.sqrt(Math.min(size.x, size.y));
            for (let i = 0; i < states.length; i++) {
                const id = `${GameStyle.MODULE}-${orientation}-${GameStyle.TEST_BUTTON}000${i}`;
                if (!utils.TextureCache[id]) {
                    const graphics = GraphicsUtil.createRoundedRectangle(size, radius, states[i], 1);
                    const texture = this.renderer.getRenderer().generateTexture(graphics);
                    Texture.addToCache(texture, id);
                }
            }
        }
    }
    handleFreeSpinAwarded() {
        const index = RandomUtil.nextInt(0, 5);
        const event = GameTestEvent.FREE_SPIN_OVERLAY;
        event.setPayload(index);
        this.dispatchGame(event);
    }
    handleTotalWin() {
        const winAmount = RandomUtil.nextInt(10, 1000);
        this.model.setTotalWin(winAmount);
        this.dispatchGame(GameTestEvent.TOTAL_WIN_UPDATE);
    }
    handleShow() {
        this.showButton.renderable = false;
        this.panel.renderable = true;
        for (const button of this.testButtons) {
            button.setEnabled(true);
        }
    }
    handleHide() {
        this.panel.renderable = false;
        this.showButton.renderable = true;
        this.showButton.setEnabled(true);
    }
    handleChanceGame() {
        const chanceGameFeatureConfig = this.model.getFeatureConfig().getChanceGameFeatureConfig();
        let result = this.model.getSpinResult();
        if (!result) {
            result = new ExtendedSpinResultData(undefined);
        }
        const winData = new SymbolWinData(undefined);
        winData.setNumberOfSymbols(3);
        winData.setAmount(400);
        winData.setType(SymbolType.JOKER);
        this.model.setScatterCount(RandomUtil.nextInt(3, 5));
        const symbolWins = new Dictionary();
        symbolWins.setValue(SymbolType.JOKER, winData);
        result['symbolWins'] = symbolWins;
        const ids = chanceGameFeatureConfig.getIds();
        const id = ids[this.model.getScatterCount() - 3];
        const multipliers = chanceGameFeatureConfig.getMultipliers(id);
        const multiplier = multipliers[RandomUtil.nextInt(0, multipliers.length)];
        const chanceGame = new ChanceGameData(new DataObject({
            [ApiConstants.PROPERTY_ID]: 0,
            [ApiConstants.PROPERTY_STAKE]: this.model.getStake().getCurrent(),
            [ApiConstants.PROPERTY_TOTAL_WIN]: multiplier * this.model.getStake().getCurrent(),
            [ApiConstants.PROPERTY_CURRENCY]: "GBP",
            [ApiConstants.PROPERTY_PARAMS]: {
                [ApiConstants.PROPERTY_MULTIPLIER]: multiplier
            },
            [ApiConstants.PROPERTY_GAME_CONFIG]: {
                [ApiConstants.PROPERTY_ID]: id,
                [ApiConstants.PROPERTY_MULTIPLIER]: 1,
            }
        }));
        result['chanceGame'] = chanceGame;
        this.model.setSpinResult(result);
        this.dispatchGame(SlingoEvent.SCATTER_WIN_ANIMATION);
    }
    handleChanceGameMajor() {
        const chanceGameFeatureConfig = this.model.getFeatureConfig().getChanceGameFeatureConfig();
        let result = this.model.getSpinResult();
        if (!result) {
            result = new ExtendedSpinResultData(undefined);
        }
        const winData = new SymbolWinData(undefined);
        winData.setNumberOfSymbols(3);
        winData.setAmount(400);
        winData.setType(SymbolType.JOKER);
        this.model.setScatterCount(RandomUtil.nextInt(3, 5));
        const symbolWins = new Dictionary();
        symbolWins.setValue(SymbolType.JOKER, winData);
        result['symbolWins'] = symbolWins;
        const ids = chanceGameFeatureConfig.getIds();
        const id = ids[this.model.getScatterCount() - 3];
        const multipliers = chanceGameFeatureConfig.getMultipliers(id);
        const multiplier = multipliers[multipliers.length - 1];
        const chanceGame = new ChanceGameData(new DataObject({
            [ApiConstants.PROPERTY_ID]: 0,
            [ApiConstants.PROPERTY_STAKE]: this.model.getStake().getCurrent(),
            [ApiConstants.PROPERTY_TOTAL_WIN]: multiplier * this.model.getStake().getCurrent(),
            [ApiConstants.PROPERTY_CURRENCY]: "GBP",
            [ApiConstants.PROPERTY_PARAMS]: {
                [ApiConstants.PROPERTY_MULTIPLIER]: multiplier
            },
            [ApiConstants.PROPERTY_GAME_CONFIG]: {
                [ApiConstants.PROPERTY_ID]: id,
                [ApiConstants.PROPERTY_MULTIPLIER]: 1,
            }
        }));
        result['chanceGame'] = chanceGame;
        this.model.setSpinResult(result);
        this.dispatchGame(SlingoEvent.SCATTER_WIN_ANIMATION);
    }
    handleGameOverSelected() {
        this.model.getResultData().reset();
        //ResultData.reset() forgets to reset the symbol win. Nice. 
        this.model.getResultData().setSymbolWin(0);
        super.handleGameOverSelected();
    }
    handleWinLine() {
        const winLine = this.model.getWinLine();
        if (!winLine) {
            const symbols = new Array(RandomUtil.nextInt(3, 5)).fill(GameStyle.JOKER);
            for (let i = symbols.length; i < this.model.getReelDisplayData().getNumberOfReels(); ++i) {
                symbols.push(GameStyle.FREE_SPIN);
            }
            const spinResult = this.model.getSpinResult() ?? new ExtendedSpinResultData(new DataObject({
                [ApiConstants.PROPERTY_REEL_SEQUENCE]: {
                    [ApiConstants.PROPERTY_SYMBOLS]: symbols,
                    [ApiConstants.PROPERTY_COIN_INDEXES]: [],
                    [ApiConstants.PROPERTY_FREE_SPIN_INDEXES]: [],
                    [ApiConstants.PROPERTY_JOKER_INDEXES]: [],
                    [ApiConstants.PROPERTY_SUPER_JOKER_INDEXES]: [],
                    [ApiConstants.PROPERTY_DEVIL_INDEXES]: [],
                    [ApiConstants.PROPERTY_COLLECTABLE_INDEXES]: [],
                    [ApiConstants.PROPERTY_BONUS_INDEXES]: [],
                },
            }));
            const multiplier = this.model.getFeatureConfig().getWinLineFeatureConfig().getMultipliers(SymbolType.JOKER)[symbols.length] ?? 1;
            const stake = this.model.getStake()?.getCurrent() ?? 1;
            const amount = stake * multiplier;
            const winLine = new WinLineData(new DataObject({
                [ApiConstants.PROPERTY_AMOUNT]: amount,
                [ApiConstants.PROPERTY_SYMBOLS]: symbols
            }));
            spinResult["winLine"] = winLine;
            console.log("Generated test win line:", winLine);
            this.model.setSpinResult(spinResult);
        }
        this.dispatchGame(GameEvent.WIN_LINE_ANIMATION);
    }
    updateBigWinResult() {
        super.updateBigWinResult();
        this.model.setTotalWin((this.model.getStake()?.getCurrent() ?? 1) * 500);
    }
    updateSlingoOnlyResult() {
        let result = this.model.getResultData();
        result.reset();
        result.setSlingoWin(1200);
        result.setNoWin(false);
        result.setJokerWin(undefined);
        result.setCoinWin(undefined);
        this.model.setSpinResult(new ExtendedSpinResultData(new DataObject()));
        this.model.getSpinResult().debug_setCollectWinnings(new CollectWinResultData(new DataObject({
            [ApiConstants.PROPERTY_SCATTER_WIN]: 5,
        })));
    }
    updateSlingoAndJokerResult() {
        let result = this.model.getResultData();
        result.reset();
        result.setSlingoWin(1200);
        result.setNoWin(false);
        let jokerWin = new SymbolWinData();
        jokerWin.setAmount(300);
        jokerWin.setType(SymbolType.JOKER);
        jokerWin.setNumberOfSymbols(3);
        result.setJokerWin(jokerWin);
        this.model.setSpinResult(new ExtendedSpinResultData(new DataObject()));
        this.model.getSpinResult().debug_setCollectWinnings(new CollectWinResultData(new DataObject({
            [ApiConstants.PROPERTY_SCATTER_WIN]: 5,
            [ApiConstants.PROPERTY_WINLINE_WIN]: 10,
        })));
    }
    handleGameSummarySelected() {
        this._gameSummaryVariant = (this._gameSummaryVariant + 1) % 3;
        this.model.setSpinResult(new ExtendedSpinResultData(new DataObject()));
        this.model.getSpinResult().debug_setCollectWinnings(new CollectWinResultData(new DataObject()));
        switch (this._gameSummaryVariant) {
            case 0:
                this.updateStandardResult();
                break;
            case 1:
                this.updateSlingoOnlyResult();
                break;
            case 2:
                this.updateSlingoAndJokerResult();
                break;
        }
        this.dispatchGame(TestEvent.GAME_SUMMARY);
    }
    handleSlingoOnlyResult() {
        let result = this.model.getResultData();
        result.reset();
        this.model.setSpinResult(new ExtendedSpinResultData(new DataObject()));
        this.model.getSpinResult().debug_setCollectWinnings(new CollectWinResultData(new DataObject()));
        this.updateStandardResult();
        this.dispatchGame(TestEvent.GAME_SUMMARY);
    }
    handleSlingoAndScatterResult() {
        let result = this.model.getResultData();
        result.reset();
        this.updateSlingoOnlyResult();
        this.dispatchGame(TestEvent.GAME_SUMMARY);
    }
    handleSlingoScatterAndWinLineResult() {
        let result = this.model.getResultData();
        result.reset();
        this.updateSlingoAndJokerResult();
        this.dispatchGame(TestEvent.GAME_SUMMARY);
    }
}
//# sourceMappingURL=GameTest.js.map